import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Lock, ArrowRight, Loader2, KeyRound } from "lucide-react";
import { LogoPair } from "@/components/LogoPair";
import { useAdminLtrLock } from "@/hooks/useAdminLtrLock";

export default function AdminLogin() {
  useAdminLtrLock();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetStep, setResetStep] = useState("request"); // "request" | "complete"
  const [resetBusy, setResetBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Auto-retry transient backend warmup errors after a deploy (Cloudflare 5xx,
      // ERR_NETWORK, timeouts). Wrong-password 401s are NOT retried.
      const _isTransient = (err) => {
        if (!err) return false;
        if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED" || err.code === "ERR_BAD_RESPONSE") return true;
        const s = err.response?.status;
        return typeof s === "number" && s >= 500 && s <= 599;
      };
      const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let res;
      const delays = [2000, 4000];
      for (let i = 0; i < 3; i++) {
        try {
          res = await axios.post(`${API}/admin/login`, { email, password });
          break;
        } catch (err) {
          if (i === 2 || !_isTransient(err)) throw err;
          // eslint-disable-next-line no-console
          console.warn(`[login] attempt ${i + 1}/3 failed, retrying in ${delays[i]}ms…`);
          await _sleep(delays[i]);
        }
      }
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_email", res.data.email);
      localStorage.setItem("admin_role", res.data.role || "admin");
      localStorage.setItem("admin_name", res.data.name || "Admin");
      toast.success(`Welcome ${res.data.name || "back"}`);
      navigate("/admin/dashboard");
    } catch (err) {
      let msg;
      if (err.code === "ECONNABORTED") {
        msg = "Login timed out. Server may be slow — please try again.";
      } else if (err.code === "ERR_NETWORK") {
        msg = "Network connection lost. Please check your internet and try again.";
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        msg = "Wrong email or password. Please type them carefully (passwords are case-sensitive).";
      } else if (err.response?.status === 423) {
        // 24h reviewer access window expired
        msg = err.response?.data?.detail || "Your access window has expired. Please contact the admin.";
      } else if (err.response?.status === 429) {
        msg = "Too many failed login attempts. Please wait 5 minutes and try again.";
      } else if (err.response?.status >= 500) {
        msg = `Server error ${err.response.status}. The backend may be restarting — please retry in 30 seconds.`;
      } else if (err.response?.data?.detail) {
        msg = `Login failed: ${err.response.data.detail}`;
      } else {
        msg = `Login failed (${err.message || "unknown"}). Please retry.`;
      }
      toast.error(msg, { duration: 10000 });
      // eslint-disable-next-line no-console
      console.error("[login] failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 py-16" dir="ltr" lang="en" data-testid="admin-login-root">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full"
      >
        <div className="flex flex-col items-center gap-4 mb-8">
          {/* Venue */}
          <span className="font-heading font-bold text-[#F5C518] text-base tracking-[0.28em] uppercase">
            VENUE
          </span>
          <div className="flex flex-row items-center justify-center gap-3">
            <img
              src="/logos/kinan-combined.png?v=1"
              alt="Kinan"
              className="h-[40px] w-auto object-contain"
              style={{ filter: "invert(1)" }}
            />
            <div className="h-[34px] w-px bg-white/70" />
            <img
              src="/logos/rimal-only.png?v=1"
              alt="Rimal Center"
              className="h-[40px] w-auto object-contain"
              style={{ filter: "invert(1)" }}
            />
          </div>

          {/* Partners • Sponsors */}
          <span className="font-heading font-bold text-[#F5C518] text-base tracking-[0.28em] uppercase mt-2">
            PARTNERS • SPONSORS
          </span>
          <div className="flex flex-row flex-wrap items-center justify-center gap-6 max-w-full">
            <img
              src="/logos/crewznation-hero.png?v=2"
              alt="CrewZ Nation"
              className="h-[38.7px] w-auto object-contain"
            />
            <img
              src="/logos/sms.png?v=3"
              alt="SMS"
              className="h-[30.8px] w-auto object-contain"
            />
            <img
              src="/logos/fahrers-hero.png"
              alt="The Fahrers"
              className="h-[22px] w-auto object-contain"
            />
          </div>
          <div className="flex flex-row flex-wrap items-center justify-center gap-6">
            <img
              src="/logos/incas.png?v=3"
              alt="Incas"
              className="h-[52px] w-auto object-contain"
              style={{ filter: "invert(1)" }}
            />
            <img
              src="/logos/sto-performance.png?v=8"
              alt="STO Performance"
              className="h-[50px] w-auto object-contain max-w-full"
            />
            <img
              src="/logos/siyak.png?v=6"
              alt="Siyak Alruyah Alalamiyah"
              className="h-[56px] w-auto object-contain"
            />
            <img
              src="/logos/almurad.png?v=2"
              alt="Almurad — Specialists in Rim Repair"
              className="h-[56px] w-auto object-contain -ml-[8px]"
            />
          </div>
        </div>

        <div className="border border-white/10 bg-white/[0.02] p-8 sm:p-10">
          <div className="w-12 h-12 border border-[#F5C518]/40 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-5 h-5 text-[#F5C518]" />
          </div>

          <div className="label-kicker text-center mb-3">Private Access</div>
          <h1 className="font-heading font-extrabold text-white text-3xl text-center mb-2">
            Admin <span className="text-[#F5C518]">Portal</span>
          </h1>
          <p className="font-body text-white/40 text-sm text-center mb-8">
            Management dashboard for applications
          </p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="label-kicker block mb-2">Email</label>
              <input
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@crewznation.com"
                required
                className="w-full bg-transparent border-b border-white/15 focus:border-[#F5C518] text-white py-2 outline-none transition-colors font-body"
              />
            </div>
            <div>
              <label className="label-kicker block mb-2">Password</label>
              <input
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full bg-transparent border-b border-white/15 focus:border-[#F5C518] text-white py-2 outline-none transition-colors font-body"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 mt-8"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Authenticating
                </>
              ) : (
                <>
                  Sign In <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => { setForgotMode(true); setResetStep("request"); setResetEmail(email); }}
              data-testid="forgot-password-link"
              className="block mx-auto mt-2 text-xs text-white/50 hover:text-[#F5C518] tracking-[0.15em] uppercase transition-colors"
            >
              Forgot password?
            </button>
          </form>
        </div>

        {/* Password reset modal */}
        {forgotMode && (
          <div
            data-testid="forgot-password-modal"
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-6"
            onClick={(e) => { if (e.target === e.currentTarget) setForgotMode(false); }}
          >
            <div className="max-w-md w-full border border-[#F5C518]/40 bg-[#050505] p-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 border border-[#F5C518]/40 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-[#F5C518]" />
                </div>
              </div>
              <h2 className="font-heading font-extrabold text-white text-2xl text-center mb-2">
                Reset <span className="text-[#F5C518]">Password</span>
              </h2>

              {resetStep === "request" ? (
                <>
                  <p className="font-body text-white/50 text-sm text-center mb-6">
                    Enter your authorized admin email. If it&apos;s recognized, a single-use
                    reset token will be prepared. The owner will hand it to you securely.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="label-kicker block mb-2">Email</label>
                      <input
                        type="email"
                        data-testid="reset-email-input"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="you@crewznation.com"
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#F5C518] text-white py-2 outline-none font-body"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={resetBusy || !resetEmail}
                      data-testid="reset-request-btn"
                      onClick={async () => {
                        setResetBusy(true);
                        try {
                          await axios.post(`${API}/admin/password-reset/request`, { email: resetEmail });
                          toast.success("If this address is authorized, a reset has been prepared.");
                          setResetStep("complete");
                        } catch {
                          // Generic path — never reveal whether email is allowlisted.
                          toast.success("If this address is authorized, a reset has been prepared.");
                          setResetStep("complete");
                        } finally {
                          setResetBusy(false);
                        }
                      }}
                      className="btn-primary w-full inline-flex items-center justify-center gap-2"
                    >
                      {resetBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Requesting</>) : (<>Request reset token <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-body text-white/50 text-sm text-center mb-6">
                    Enter the token you received from the owner along with a new
                    password (12+ chars, upper/lower/digit/symbol).
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="label-kicker block mb-2">Email</label>
                      <input
                        type="email"
                        data-testid="reset-email2-input"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#F5C518] text-white py-2 outline-none font-body"
                      />
                    </div>
                    <div>
                      <label className="label-kicker block mb-2">Reset Token</label>
                      <input
                        type="text"
                        data-testid="reset-token-input"
                        value={resetToken}
                        onChange={(e) => setResetToken(e.target.value)}
                        placeholder="paste the token"
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#F5C518] text-white py-2 outline-none font-body"
                      />
                    </div>
                    <div>
                      <label className="label-kicker block mb-2">New Password</label>
                      <input
                        type="password"
                        data-testid="reset-newpw-input"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#F5C518] text-white py-2 outline-none font-body"
                      />
                    </div>
                    <div>
                      <label className="label-kicker block mb-2">Confirm Password</label>
                      <input
                        type="password"
                        data-testid="reset-confirmpw-input"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#F5C518] text-white py-2 outline-none font-body"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={resetBusy || !resetEmail || !resetToken || !newPassword || newPassword !== confirmPassword}
                      data-testid="reset-complete-btn"
                      onClick={async () => {
                        if (newPassword !== confirmPassword) {
                          toast.error("Passwords do not match.");
                          return;
                        }
                        setResetBusy(true);
                        try {
                          await axios.post(`${API}/admin/password-reset/complete`, {
                            email: resetEmail,
                            token: resetToken,
                            new_password: newPassword,
                          });
                          toast.success("Password set. You can now sign in.");
                          setForgotMode(false);
                          setResetStep("request");
                          setResetToken(""); setNewPassword(""); setConfirmPassword("");
                          setEmail(resetEmail);
                        } catch (err) {
                          const detail = err.response?.data?.detail || "Reset failed.";
                          toast.error(detail);
                        } finally {
                          setResetBusy(false);
                        }
                      }}
                      className="btn-primary w-full inline-flex items-center justify-center gap-2"
                    >
                      {resetBusy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Setting</>) : (<>Set new password <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </>
              )}

              <button
                type="button"
                onClick={() => { setForgotMode(false); setResetStep("request"); }}
                className="block mx-auto mt-6 text-xs text-white/40 hover:text-white/70 tracking-[0.15em] uppercase"
              >
                ← Back to sign in
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => navigate("/")}
          className="block mx-auto mt-8 text-xs text-white/30 hover:text-white/60 tracking-[0.15em] uppercase"
        >
          ← Back to home
        </button>
      </motion.div>
    </div>
  );
}
